import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { NavBar } from "../common/NavBar";
import { Dispatcher } from "./Sites/SiteAboutPage/SiteAboutPageUtils/siteAboutInterfaces";

export type MissingResource = "site" | "camera";

export interface ResourceNotFoundState {
  resource?: MissingResource;
  id?: string | number;
}

const buildCopy = (resource: MissingResource | undefined, id?: string | number) => {
  const idSuffix = id ? ` ${id}` : "";
  if (resource === "site") {
    return {
      heading: "Site not found",
      body: `Site${idSuffix} doesn't exist, or you no longer have access to it. It may have been deleted, or moved to another account.`,
    };
  }
  if (resource === "camera") {
    return {
      heading: "Camera not found",
      body: `Camera${idSuffix} doesn't exist, or you no longer have access to it. It may have been deleted, or moved to another site.`,
    };
  }
  return {
    heading: "Not found",
    body: "The page you're looking for isn't available. It may have been deleted, or you may no longer have access to it.",
  };
};

/**
 * Shown when a link points at a site or camera that the API can't return —
 * deleted, or outside the signed-in user's access. Previously these 404s fell
 * through the error handler and left the page body unrendered, so the customer
 * just got an empty screen.
 */
export const ResourceNotFound = ({
  navBarCollapsed,
  setNavBarCollapsed,
}: {
  navBarCollapsed: boolean;
  setNavBarCollapsed: Dispatcher<boolean>;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { resource, id } = (location.state || {}) as ResourceNotFoundState;
  const { heading, body } = buildCopy(resource, id);

  return (
    <div>
      <div className="bg-actuate-grey absolute w-screen h-screen">
        <div className="relative top-[140px] mx-auto max-w-[600px] text-center px-6">
          <p className="text-mulish text-actuate-blue text-[80px] leading-none">
            404
          </p>
          <p className="text-mulish text-actuate-blue text-[30px] mt-4">
            {heading}
          </p>
          <p className="text-mulish text-actuate-blue text-[16px] mt-4">
            {body}
          </p>
          <button
            className="text-mulish text-white bg-actuate-blue hover:bg-actuate-orange rounded-md mt-8 px-6 py-2 text-[16px]"
            onClick={() => navigate("/sites")}
          >
            Back to Sites
          </button>
        </div>
      </div>
      <div className="justify-start">
        <NavBar
          navBarCollapsed={navBarCollapsed}
          setNavBarCollapsed={setNavBarCollapsed}
          props={{}}
        />
      </div>
    </div>
  );
};
